import React, { useState, useEffect, useRef } from 'react';
import { Table, Card, Tag, Modal, Space, Button, message, Input, Select, Popconfirm, Checkbox, Dropdown, Typography } from 'antd';
import { EyeOutlined, SearchOutlined, CheckCircleOutlined, CloseCircleOutlined, FilePdfOutlined, SettingOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import authService from '../utils/auth';

// 一日游销售提成：每人 $10，与 EKOK 录单 Purchase 的扣减一致
const DAYTOUR_SALES_COMMISSION = 10;
const PAY_METHOD_LABEL = { wechat: '微信支付', alipay: '支付宝', polipay: 'POLi', upi_upop: '银联', transfer: '银行转账', wallet: '钱包余额支付' };

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

function BillingStatistics() {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedTour, setSelectedTour] = useState(null);
  const [tourists, setTourists] = useState([]);
  const [touristLoading, setTouristLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [dateFilter, setDateFilter] = useState(null);
  const [settledFilter, setSettledFilter] = useState('unsettled'); // all | settled | unsettled
  const [exporting, setExporting] = useState(false);
  const [ekokMatchTourIds, setEkokMatchTourIds] = useState(null); // null=未搜索, []=无结果
  const [ekokSearching, setEkokSearching] = useState(false);
  const allColumnKeys = ['orderNumber', 'ekok', 'salesName', 'payment', 'totalAmount', 'totalCommission', 'totalCount', 'normalCount', 'discountCount', 'refundedCount'];
  const [visibleColumns, setVisibleColumns] = useState(['ekok', 'payment', 'totalAmount', 'totalCommission', 'totalCount']);
  const modalContentRef = useRef(null);

  useEffect(() => {
    fetchTours();
  }, []);

  const fetchTours = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/tours`, {
        params: { tourType: 'daytour' },
        headers: authService.getAuthHeaders()
      });
      setTours(response.data.data || []);
    } catch (error) {
      message.error('获取出团列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchTourists = async (tourId) => {
    setTouristLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/tourists/tour/${tourId}`, {
        headers: authService.getAuthHeaders()
      });
      setTourists(response.data.data || []);
    } catch (error) {
      message.error('获取游客详情失败');
    } finally {
      setTouristLoading(false);
    }
  };

  const handleViewDetail = (tour) => {
    setSelectedTour(tour);
    setModalVisible(true);
    fetchTourists(tour.id);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setSelectedTour(null);
    setTourists([]);
    setExpandedKeys([]);
  };

  const handleSettle = async (settled) => {
    try {
      await axios.post(`${API_BASE}/tourists/settle-order`, {
        tourId: selectedTour.id,
        settled
      }, { headers: authService.getAuthHeaders() });
      message.success(settled ? '已结算' : '已取消结算');
      // 更新 selectedTour 状态
      setSelectedTour(prev => ({ ...prev, billingSettled: settled, billingSettledAt: settled ? new Date() : null }));
      fetchTours(); // 刷新列表
    } catch (error) {
      message.error(error.response?.data?.error || '操作失败');
    }
  };

  const handleExportPdf = async () => {
    const el = modalContentRef.current;
    if (!el) return;
    setExporting(true);
    // 临时显示全部列
    const prevColumns = visibleColumns;
    setVisibleColumns(allColumnKeys);
    // 等待 React 渲染完成
    await new Promise(r => setTimeout(r, 300));
    try {
      const canvas = await html2canvas(el, { scale: 1.5, useCORS: true, backgroundColor: '#fff' });
      const imgData = canvas.toDataURL('image/jpeg', 0.75);
      const imgW = canvas.width;
      const imgH = canvas.height;
      const pdfW = 297; // A4 landscape width mm
      const pdfH = 210; // A4 landscape height mm
      const margin = 5;
      // 单页：宽度优先撑满，高度超出则缩放适应
      const availW = pdfW - margin * 2;
      const availH = pdfH - margin * 2;
      const ratio = Math.min(availW / imgW, availH / imgH);
      const w = imgW * ratio;
      const h = imgH * ratio;
      const pdf = new jsPDF('l', 'mm', 'a4');
      pdf.addImage(imgData, 'JPEG', (pdfW - w) / 2, (pdfH - h) / 2, w, h);
      const fileName = `账单_${selectedTour?.destination?.name || ''}_${selectedTour?.productName || ''}_${moment(selectedTour?.departureDate).format('YYYYMMDD')}.pdf`;
      pdf.save(fileName);
    } catch (err) {
      message.error('导出PDF失败');
      console.error(err);
    } finally {
      setVisibleColumns(prevColumns);
      setExporting(false);
    }
  };

  const searchEkok = async (value) => {
    if (!value || !value.trim()) {
      setEkokMatchTourIds(null);
      return;
    }
    setEkokSearching(true);
    try {
      const response = await axios.get(`${API_BASE}/tourists`, {
        headers: authService.getAuthHeaders()
      });
      if (response.data.success) {
        const lower = value.toLowerCase().trim();
        const matched = response.data.data.filter(t =>
          t.ekok?.toLowerCase().includes(lower)
        );
        setEkokMatchTourIds([...new Set(matched.map(t => t.tourId))]);
      }
    } catch {
      // ignore
    } finally {
      setEkokSearching(false);
    }
  };

  // 过滤出团列表
  const filteredTours = tours.filter(tour => {
    let match = true;
    if (searchText) {
      const lower = searchText.toLowerCase();
      const textMatch = (
        tour.productName?.toLowerCase().includes(lower) ||
        tour.destination?.name?.toLowerCase().includes(lower)
      );
      const ekokMatch = ekokMatchTourIds !== null && ekokMatchTourIds.includes(tour.id);
      match = match && (textMatch || ekokMatch);
    }
    if (dateFilter) {
      const tourDate = moment(tour.departureDate).format('YYYY-MM-DD');
      match = match && tourDate === dateFilter;
    }
    if (settledFilter === 'settled') {
      match = match && tour.billingSettled && tour.status !== 'cancelled';
    } else if (settledFilter === 'unsettled') {
      match = match && !tour.billingSettled && tour.status !== 'cancelled';
    } else if (settledFilter === 'cancelled') {
      match = match && tour.status === 'cancelled';
    }
    return match;
  });

  // 过滤掉已取消的游客（保留退款的用于显示退款记录），按订单号分组
  const activeTourists = tourists.filter(t => t.orderStatus !== 'cancelled');

  /*
   * 单价与提成按人取（2026-10-28 与会员系统统一）：
   *   · 票价：游客身上的 price（会员系统推送的实付票价）优先，没有才用团上设置的原价/优惠价
   *   · 销售提成：团上设置了就用团上的；一日游未设置时按每人 $10（全部一日游统一）
   */
  const num = (v) => (v == null || v === '' ? null : Number(v));
  const unitPrice = (m) => num(m.price) ?? num(m.discountPrice ? selectedTour?.seniorPrice : selectedTour?.adultPrice);
  const unitCommission = (m) => num(m.discountPrice ? selectedTour?.seniorCommission : selectedTour?.adultCommission)
    ?? (selectedTour?.tourType === 'daytour' ? DAYTOUR_SALES_COMMISSION : null);

  const buildOrderGroups = () => {
    const groups = {};
    activeTourists.forEach(t => {
      const key = t.orderNumber || `_solo_${t.id}`;
      if (!groups[key]) {
        groups[key] = {
          orderNumber: t.orderNumber || '-',
          ekok: t.ekok || '-',
          salesName: t.salesName || '-',
          payments: t.payments || [],
          members: [],
        };
      }
      groups[key].members.push(t);
    });

    return Object.values(groups).map((g, idx) => {
      const activeMembers = g.members.filter(m => m.orderStatus !== 'refunded');
      const refundedMembers = g.members.filter(m => m.orderStatus === 'refunded');
      const normalCount = activeMembers.filter(m => !m.discountPrice).length;
      const discountCount = activeMembers.filter(m => m.discountPrice).length;
      const refundedAmountSum = refundedMembers.reduce((s, m) => s + (Number(m.refundAmount) || 0), 0);
      const totalAmount = activeMembers.reduce((s, m) => s + (unitPrice(m) || 0), 0) - refundedAmountSum;
      const totalCommission = activeMembers.reduce((s, m) => s + (unitCommission(m) || 0), 0)
        - refundedMembers.reduce((s, m) => s + (unitCommission(m) || 0), 0);
      return {
        key: `group_${idx}`,
        orderNumber: g.orderNumber,
        ekok: g.ekok,
        totalCount: activeMembers.length,
        normalCount,
        discountCount,
        refundedCount: refundedMembers.length,
        totalAmount,
        totalCommission,
        salesName: g.salesName,
        payments: g.payments,
        members: g.members,
      };
    });
  };

  const orderGroups = modalVisible ? buildOrderGroups() : [];

  // ========== 出团列表列 ==========
  const tourColumns = [
    {
      title: '产品名称',
      key: 'destination',
      width: 180,
      render: (_, record) => record.destination?.name || '-',
    },
    {
      title: '团号',
      dataIndex: 'productName',
      key: 'productName',
      width: 160,
    },
    {
      title: '出发日期',
      dataIndex: 'departureDate',
      key: 'departureDate',
      width: 120,
      render: (date) => moment(date).format('YYYY-MM-DD'),
      sorter: (a, b) => new Date(a.departureDate) - new Date(b.departureDate),
      defaultSortOrder: 'descend',
    },
    {
      title: '游客总数',
      key: 'totalTourists',
      width: 100,
      align: 'center',
      render: (_, record) => record.totalTourists ?? '-',
    },
    {
      title: '状态',
      key: 'status',
      width: 120,
      align: 'center',
      render: (_, record) => {
        if (record.status === 'cancelled') return <Tag color="red">已取消</Tag>;
        return record.billingSettled
          ? <Tag color="green" icon={<CheckCircleOutlined />}>已结算</Tag>
          : <Tag color="default">未结算</Tag>;
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record)}
        >
          查看详情
        </Button>
      ),
    },
  ];

  // ========== 订单分组列 ==========
  const [expandedKeys, setExpandedKeys] = useState([]);

  const toggleExpand = (key) => {
    setExpandedKeys(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const orderColumns = [
    {
      title: '订单号',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      width: 110,
      render: (text, record) => (
        <a onClick={() => toggleExpand(record.key)} style={{ fontFamily: 'monospace', cursor: 'pointer' }}>{text}</a>
      ),
    },
    {
      title: 'EKOK',
      dataIndex: 'ekok',
      key: 'ekok',
      width: 80,
      render: (text) => <span style={{ color: text && text !== '-' ? '#1890ff' : '#999', fontWeight: text && text !== '-' ? 'bold' : 'normal' }}>{text}</span>,
    },
    {
      title: '销售',
      dataIndex: 'salesName',
      key: 'salesName',
      width: 35,
    },
    {
      title: '收款方式',
      dataIndex: 'payments',
      key: 'payment',
      width: 130,
      render: (list) => (list?.length ? list.map((p, i) => (
        <div key={i}>
          <div>{PAY_METHOD_LABEL[p.method] || p.method}</div>
          {p.txnId && <Typography.Text type="secondary" copyable style={{ fontSize: 12, fontFamily: 'monospace' }}>{p.txnId}</Typography.Text>}
          {p.method === 'transfer' && p.payeeSuffix && (
            <Tag color={p.payeeKnown ? 'blue' : 'orange'} title={p.payeeAccount} style={{ marginTop: 2 }}>
              {p.payeeName ? `转入 ${p.payeeName}` : `转入 -${p.payeeSuffix}`}{p.payeeKnown ? '' : ' 未登记账号'}
            </Tag>
          )}
          {p.method === 'transfer' && !p.payeeSuffix && p.hasProof && <Tag style={{ marginTop: 2 }}>账号未识别</Tag>}
        </div>
      )) : <span style={{ color: '#999' }}>-</span>),
    },
    {
      title: '应收总金额',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 80,
      align: 'right',
      render: (val) => val > 0 ? <b>${val.toFixed(2)}</b> : val < 0 ? <b style={{ color: '#ff4d4f' }}>-${Math.abs(val).toFixed(2)}</b> : '-',
    },
    {
      title: '销售提成',
      dataIndex: 'totalCommission',
      key: 'totalCommission',
      width: 80,
      align: 'right',
      render: (val) => val > 0 ? <span style={{ color: '#52c41a', fontWeight: 'bold' }}>${val.toFixed(2)}</span> : val < 0 ? <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>-${Math.abs(val).toFixed(2)}</span> : '-',
    },
    {
      title: '游客数',
      dataIndex: 'totalCount',
      key: 'totalCount',
      width: 70,
      align: 'center',
    },
    {
      title: selectedTour?.adultPrice ? `原价($${Number(selectedTour.adultPrice).toFixed(0)})` : '原价',
      dataIndex: 'normalCount',
      key: 'normalCount',
      width: 80,
      align: 'center',
      render: (count) => count > 0 ? count : <span style={{ color: '#999' }}>0</span>,
    },
    {
      title: selectedTour?.seniorPrice ? `优惠($${Number(selectedTour.seniorPrice).toFixed(0)})` : '优惠',
      dataIndex: 'discountCount',
      key: 'discountCount',
      width: 80,
      align: 'center',
      render: (count) => count > 0 ? <span style={{ color: '#ff4d4f' }}>{count}</span> : <span style={{ color: '#999' }}>0</span>,
    },
    {
      title: '退款',
      dataIndex: 'refundedCount',
      key: 'refundedCount',
      width: 70,
      align: 'center',
      render: (count) => count > 0 ? <span style={{ color: '#722ed1', fontWeight: 'bold' }}>{count}</span> : <span style={{ color: '#999' }}>0</span>,
    },
  ];

  // ========== 展开行：订单内游客 ==========
  const expandedRowRender = (record) => {
    const memberColumns = [
      {
        title: '游客姓名',
        dataIndex: 'touristName',
        key: 'touristName',
        width: 180,
        render: (name, r) => r.orderStatus === 'refunded'
          ? <span style={{ color: '#722ed1' }}>{name}</span>
          : name,
      },
      {
        title: '类型',
        key: 'priceType',
        width: 60,
        render: (_, r) => r.orderStatus === 'refunded'
          ? <span style={{ color: '#722ed1' }}>退款</span>
          : r.discountPrice
            ? <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>优惠</span>
            : <span>原价</span>,
      },
      {
        title: '售价',
        key: 'price',
        width: 80,
        align: 'right',
        render: (_, r) => {
          const price = unitPrice(r);
          if (price == null) return '-';
          if (r.orderStatus === 'refunded') {
            const refund = Number(r.refundAmount) || 0;
            return <span style={{ color: '#ff4d4f' }}>-${refund.toFixed(2)}</span>;
          }
          return `$${Number(price).toFixed(2)}`;
        },
      },
      {
        title: '提成',
        key: 'commission',
        width: 80,
        align: 'right',
        render: (_, r) => {
          const comm = unitCommission(r);
          if (comm == null) return '-';
          if (r.orderStatus === 'refunded') return <span style={{ color: '#ff4d4f' }}>-${Number(comm).toFixed(2)}</span>;
          return <span style={{ color: '#52c41a' }}>${Number(comm).toFixed(2)}</span>;
        },
      },
      {
        title: '备注',
        dataIndex: 'remarks',
        key: 'remarks',
        render: (text) => text ? <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{text}</span> : '-',
      },
    ];

    return (
      <Table
        columns={memberColumns}
        dataSource={record.members}
        rowKey="id"
        size="small"
        pagination={false}
      />
    );
  };

  // 汇总
  const filteredActiveTourists = orderGroups.flatMap(g => g.members);
  const totalAmount = orderGroups.reduce((sum, g) => g.totalAmount > 0 ? sum + g.totalAmount : sum, 0);
  const totalCommission = orderGroups.reduce((sum, g) => g.totalCommission > 0 ? sum + g.totalCommission : sum, 0);

  return (
    <div>
      <Card title="一日游账单统计">
        <Space style={{ marginBottom: 16 }} wrap>
          <Input.Search
            placeholder="搜索产品名称/团号/EKOK"
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); searchEkok(e.target.value); }}
            onSearch={searchEkok}
            style={{ width: 280 }}
            allowClear
            enterButton={<SearchOutlined />}
            loading={ekokSearching}
            onClear={() => { setSearchText(''); setEkokMatchTourIds(null); }}
          />
          <Select
            placeholder="出发日期"
            value={dateFilter}
            onChange={setDateFilter}
            style={{ width: 150 }}
            allowClear
            showSearch
            options={[...new Set(tours.map(t => moment(t.departureDate).format('YYYY-MM-DD')))]
              .sort((a, b) => b.localeCompare(a))
              .map(d => ({ value: d, label: d }))}
          />
          <Select
            value={settledFilter}
            onChange={setSettledFilter}
            style={{ width: 120 }}
            options={[
              { value: 'all', label: '全部' },
              { value: 'settled', label: '已结算' },
              { value: 'unsettled', label: '未结算' },
              { value: 'cancelled', label: '已取消' },
            ]}
          />
        </Space>

        <Table
          columns={tourColumns}
          dataSource={filteredTours}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `共 ${total} 条` }}
        />
      </Card>

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>{selectedTour?.destination?.name || ''} - {selectedTour?.productName || ''}</span>
            <Tag color="blue">{moment(selectedTour?.departureDate).format('YYYY-MM-DD')}</Tag>
            <Tag color="green">有效游客 {activeTourists.filter(t => t.orderStatus !== 'refunded').length} 人</Tag>
            {activeTourists.filter(t => t.orderStatus === 'refunded').length > 0 && (
              <Tag color="purple">退款 {activeTourists.filter(t => t.orderStatus === 'refunded').length} 人</Tag>
            )}
            {selectedTour?.guide && (
              <Tag color="purple">导游：{selectedTour.guide.name}</Tag>
            )}
          </div>
        }
        open={modalVisible}
        onCancel={handleCloseModal}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              {selectedTour?.billingSettled
                ? <Tag color="green" icon={<CheckCircleOutlined />} style={{ fontSize: 14, padding: '4px 12px' }}>已结算</Tag>
                : <Tag color="default" style={{ fontSize: 14, padding: '4px 12px' }}>未结算</Tag>
              }
            </div>
            <Space>
              <Button icon={<FilePdfOutlined />} loading={exporting} onClick={handleExportPdf}>导出PDF</Button>
              {selectedTour?.billingSettled ? (
                <Popconfirm title="确定取消结算？" onConfirm={() => handleSettle(false)} okText="确定" cancelText="取消">
                  <Button danger icon={<CloseCircleOutlined />}>取消结算</Button>
                </Popconfirm>
              ) : (
                <Popconfirm title="确定结算该团？" onConfirm={() => handleSettle(true)} okText="确定" cancelText="取消">
                  <Button type="primary" icon={<CheckCircleOutlined />} style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}>结算</Button>
                </Popconfirm>
              )}
              <Button onClick={handleCloseModal}>关闭</Button>
            </Space>
          </div>
        }
        width={900}
      >
        <div style={{ marginBottom: 8, textAlign: 'right' }}>
          <Dropdown
            trigger={['click']}
            dropdownRender={() => {
              const colLabel = (col) => typeof col.title === 'string' ? col.title : col.dataIndex === 'normalCount' ? '原价' : col.dataIndex === 'discountCount' ? '优惠' : col.key;
              return (
                <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.15)', padding: '8px 12px', minWidth: 140 }}>
                  <div style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: 6, marginBottom: 6, display: 'flex', gap: 12, fontSize: 12 }}>
                    <a onClick={() => setVisibleColumns(allColumnKeys)}>全选</a>
                    <a onClick={() => setVisibleColumns(['ekok', 'totalAmount'])}>精简</a>
                  </div>
                  {orderColumns.map(col => (
                    <div key={col.key} style={{ padding: '3px 0' }}>
                      <Checkbox
                        checked={visibleColumns.includes(col.key)}
                        onChange={(e) => {
                          setVisibleColumns(prev =>
                            e.target.checked ? [...prev, col.key] : prev.filter(k => k !== col.key)
                          );
                        }}
                      >
                        {colLabel(col)}
                      </Checkbox>
                    </div>
                  ))}
                </div>
              );
            }}
          >
            <Button size="small" icon={<SettingOutlined />}>列设置</Button>
          </Dropdown>
        </div>
        <div ref={modalContentRef}>
          <div style={{ marginBottom: 12, padding: '8px 0', textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 4 }}>
              {selectedTour?.destination?.name || ''} - {selectedTour?.productName || ''}
            </div>
            <Space size="large" style={{ fontSize: 13, color: '#666' }}>
              <span>出发日期：{moment(selectedTour?.departureDate).format('YYYY-MM-DD')}</span>
              <span>有效游客：{activeTourists.filter(t => t.orderStatus !== 'refunded').length} 人</span>
              {selectedTour?.guide && <span>导游：{selectedTour.guide.name}</span>}
              {selectedTour?.adultPrice && <span>原价：${Number(selectedTour.adultPrice).toFixed(0)}</span>}
              {selectedTour?.seniorPrice && <span>优惠价：${Number(selectedTour.seniorPrice).toFixed(0)}</span>}
            </Space>
          </div>
          {(() => {
            const filtered = orderColumns.filter(c => visibleColumns.includes(c.key));
            const displayColumns = filtered.length <= 2
              ? filtered.map(c => ({ ...c, width: undefined, align: 'center' }))
              : filtered;
            const summaryDef = {
              orderNumber: { content: '合计' },
              ekok: {},
              salesName: {},
              totalAmount: { align: 'right', content: <b>${totalAmount.toFixed(2)}</b> },
              totalCommission: { align: 'right', content: <span style={{ color: '#52c41a' }}><b>${totalCommission.toFixed(2)}</b></span> },
              totalCount: { align: 'center', content: filteredActiveTourists.filter(t => t.orderStatus !== 'refunded').length },
              normalCount: { align: 'center', content: filteredActiveTourists.filter(t => !t.discountPrice && t.orderStatus !== 'refunded').length },
              discountCount: { align: 'center', content: <span style={{ color: '#ff4d4f' }}>{filteredActiveTourists.filter(t => t.discountPrice && t.orderStatus !== 'refunded').length}</span> },
              refundedCount: { align: 'center', content: <span style={{ color: '#722ed1' }}>{filteredActiveTourists.filter(t => t.orderStatus === 'refunded').length}</span> },
            };
            return (
              <Table
                columns={displayColumns}
                dataSource={orderGroups}
                rowKey="key"
                loading={touristLoading}
                size="small"
                pagination={false}
                expandable={{
                  expandedRowRender,
                  expandedRowKeys: expandedKeys,
                  expandIcon: () => null,
                  columnWidth: 0,
                }}
                summary={() => (
                  <Table.Summary fixed>
                    <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 'bold' }}>
                      <Table.Summary.Cell index={0} />
                      {displayColumns.map((col, idx) => {
                        const def = summaryDef[col.key] || {};
                        const cellAlign = filtered.length <= 2 ? 'center' : (def.align || col.align || 'left');
                        // 第一个可见列显示"合计"
                        const content = idx === 0 && !def.content ? '合计' : (def.content || '');
                        return <Table.Summary.Cell key={col.key} index={idx + 1} align={cellAlign}>{content}</Table.Summary.Cell>;
                      })}
                    </Table.Summary.Row>
                  </Table.Summary>
                )}
              />
            );
          })()}
        </div>
      </Modal>
    </div>
  );
}

export default BillingStatistics;
